// src/components/DigitalMixHint.jsx
// Recommandation (jamais bloquante) : regrouper les œuvres numériques dans une collection qui leur est dédiée.
// Affichée sur la page d'une collection qui mélange œuvres numériques et autres œuvres, et dans l'import quand le fichier
// contient des œuvres de plateforme destinées à une collection qui contient déjà d'autres œuvres.
// Textes en français, anglais et allemand (langue de l'interface). Masquable par collection (stockage navigateur).
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";

const TEXT = {
  fr: {
    title: "Une collection dédiée à l'art numérique est plus claire",
    mixed: "Cette collection contient des œuvres numériques et d'autres œuvres. Nous vous recommandons de regrouper vos œuvres numériques dans une collection qui leur est consacrée : le consentement, le partage, l'import et la suppression se règlent alors par collection, sans toucher au reste de votre patrimoine.",
    incoming: "Les œuvres de ce fichier viennent d'une plateforme, et cette collection contient déjà d'autres œuvres. Nous vous recommandons de les importer dans une collection dédiée à l'art numérique : le consentement, le partage et l'annulation d'un import se règlent alors par collection.",
    note: "C'est une recommandation, pas une obligation.",
    ok: "Compris",
  },
  en: {
    title: "A collection dedicated to digital art is clearer",
    mixed: "This collection contains digital artworks and other works. We recommend grouping your digital artworks in a collection of their own: consent, sharing, import and deletion are then handled per collection, without touching the rest of your holdings.",
    incoming: "The works in this file come from a platform, and this collection already contains other works. We recommend importing them into a collection dedicated to digital art: consent, sharing and the undoing of an import are then handled per collection.",
    note: "This is a recommendation, not a requirement.",
    ok: "Got it",
  },
  de: {
    title: "Eine eigene Sammlung für digitale Kunst ist übersichtlicher",
    mixed: "Diese Sammlung enthält digitale Kunstwerke und andere Werke. Wir empfehlen, Ihre digitalen Kunstwerke in einer eigenen Sammlung zusammenzufassen: Einwilligung, Freigabe, Import und Löschung lassen sich dann pro Sammlung regeln, ohne den Rest Ihres Bestands zu berühren.",
    incoming: "Die Werke in dieser Datei stammen von einer Plattform, und diese Sammlung enthält bereits andere Werke. Wir empfehlen, sie in eine eigene Sammlung für digitale Kunst zu importieren: Einwilligung, Freigabe und das Rückgängigmachen eines Imports lassen sich dann pro Sammlung regeln.",
    note: "Das ist eine Empfehlung, keine Pflicht.",
    ok: "Verstanden",
  },
};

const isDigital = (a) =>
  ["nft", "digital", "digital_art"].includes(String(a?.medium_category || "").toLowerCase()) ||
  !!a?.external_source || (a?.digital_asset && typeof a.digital_asset === "object");

const storageKey = (id) => `digital-mix-hint-dismissed:${id}`;

export default function DigitalMixHint({ collectionId, incomingDigital = false }) {
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const L = TEXT[lang] || TEXT.fr;
  const [state, setState] = useState({ digital: 0, other: 0, loaded: false });
  const [hidden, setHidden] = useState(() => {
    try { return window.localStorage.getItem(storageKey(collectionId)) === "1"; } catch { return false; }
  });

  useEffect(() => {
    let alive = true;
    if (!collectionId) return undefined;
    (async () => {
      const { data } = await supabase
        .from("artworks_secure")
        .select("id, medium_category, external_source, digital_asset")
        .eq("collection_id", collectionId)
        .limit(5000);
      if (!alive) return;
      const rows = data || [];
      const digital = rows.filter(isDigital).length;
      setState({ digital, other: rows.length - digital, loaded: true });
    })().catch(() => {});
    return () => { alive = false; };
  }, [collectionId]);

  if (hidden || !state.loaded) return null;
  const mixed = state.digital > 0 && state.other > 0;
  const incoming = incomingDigital && state.other > 0;
  if (!mixed && !incoming) return null;

  const dismiss = () => {
    setHidden(true);
    try { window.localStorage.setItem(storageKey(collectionId), "1"); } catch { /* sans stockage : la recommandation reviendra */ }
  };

  return (
    <div role="note" style={{
      margin: "0 0 16px", padding: "12px 16px", borderRadius: 6, background: "#f4f1fb",
      border: "1px solid #d9d0f0", fontFamily: "system-ui, sans-serif", fontSize: 13, lineHeight: 1.5, color: "#2a2540",
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{L.title}</div>
      <div>{mixed ? L.mixed : L.incoming}</div>
      <div style={{ marginTop: 4, color: "#6b6482" }}>{L.note}</div>
      <button type="button" onClick={dismiss}
        style={{ marginTop: 8, padding: "5px 12px", border: "1px solid #cfc5ea", borderRadius: 4, background: "#fff", fontSize: 12, cursor: "pointer" }}>
        {L.ok}
      </button>
    </div>
  );
}
